<template>
  <div class="admin-page">
    <!-- 未登录 -->
    <div v-if="!isLoggedIn" class="login-wrap">
      <div class="login-card">
        <h2><el-icon><Lock /></el-icon> 信息收集管理后台</h2>
        <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef" label-position="top">
          <el-form-item label="账号" prop="username">
            <el-input v-model="loginForm.username" placeholder="管理员账号" />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input v-model="loginForm.password" type="password" placeholder="密码" show-password @keyup.enter="handleLogin" />
          </el-form-item>
          <el-button type="primary" style="width:100%" :loading="loginLoading" @click="handleLogin">登录</el-button>
        </el-form>
        <p class="login-mobile-line" @click="openMobile">
          <el-icon><Link /></el-icon> 移动端安全巡检平台 · 手机端实时查看
        </p>
        <p v-if="loginError" class="login-error">{{ loginError }}</p>
      </div>
    </div>

    <!-- 已登录 -->
    <div v-else class="admin-body">
      <!-- 移动端安全巡检平台 · 功能卡片 -->
      <div class="mobile-card">
        <div class="mc-left">
          <div class="mc-title"><el-icon><Iphone /></el-icon> 移动端安全巡检平台</div>
          <div class="mc-desc">
            电脑端「安全巡检管理」后台的移动版，领导与部门人员手机端实时查看：
            总览 / 巡检计划 / 巡检安排 / 值班领导 / 异常报告 / 巡检记录 / 隐患闭环（免登录）
          </div>
        </div>
        <div class="mc-actions">
          <el-button type="primary" @click="openMobile"><el-icon><Position /></el-icon> 打开网址</el-button>
          <el-button @click="showQr"><el-icon><FullScreen /></el-icon> 二维码</el-button>
        </div>
      </div>

      <div class="admin-header">
        <h2>信息收集管理</h2>
        <div class="header-actions">
          <el-button type="danger" :disabled="selectedIds.size === 0" @click="handleDelete">
            <el-icon><Delete /></el-icon> 删除选中 ({{ selectedIds.size }})
          </el-button>
          <el-button @click="exportCSV"><el-icon><Download /></el-icon> 导出 CSV</el-button>
          <el-button @click="exportJSON"><el-icon><Download /></el-icon> 导出 JSON</el-button>
          <el-button @click="loadRecords"><el-icon><Refresh /></el-icon> 刷新</el-button>
          <el-button type="info" @click="logout">退出</el-button>
        </div>
      </div>

      <!-- 筛选 -->
      <div class="filter-bar">
        <el-date-picker v-model="filters.startDate" type="date" placeholder="开始日期" value-format="YYYY-MM-DD" style="width:160px" />
        <el-date-picker v-model="filters.endDate" type="date" placeholder="结束日期" value-format="YYYY-MM-DD" style="width:160px" />
        <el-select v-model="filters.status" placeholder="状态" clearable style="width:140px">
          <el-option label="未处理" value="未处理" />
          <el-option label="处理中" value="处理中" />
          <el-option label="已处理" value="已处理" />
        </el-select>
        <el-select v-model="filters.synced" placeholder="同步状态" clearable style="width:140px">
          <el-option label="未同步" value="false" />
          <el-option label="已同步" value="true" />
        </el-select>
        <el-button type="primary" @click="applyFilter">筛选</el-button>
        <el-button @click="resetFilter">重置</el-button>
      </div>

      <div class="stats">共 {{ total }} 条记录，已选 {{ selectedIds.size }} 条</div>

      <!-- 列表 -->
      <div class="table-wrap">
        <el-table :data="records" stripe @selection-change="onSelectionChange" max-height="60vh">
          <el-table-column type="selection" width="50" />
          <el-table-column prop="id" label="记录码" width="160" />
          <el-table-column prop="reportTime" label="上传时间" width="180">
            <template #default="{ row }">{{ formatTime(row.reportTime) }}</template>
          </el-table-column>
          <el-table-column prop="description" label="备注" min-width="150" show-overflow-tooltip />
          <el-table-column prop="reporter" label="上传人" width="100" />
          <el-table-column label="照片" width="70" align="center">
            <template #default="{ row }">{{ (row.images || []).length }}</template>
          </el-table-column>
          <el-table-column label="视频" width="70" align="center">
            <template #default="{ row }">{{ (row.videos || []).length }}</template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="statusTag(row.status)" size="small">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="同步" width="70" align="center">
            <template #default="{ row }">
              <span :style="{ color: row.synced ? '#67c23a' : '#e6a23c' }">{{ row.synced ? '✓' : '✗' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="previewRecord(row)">查看</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pager">
        <el-pagination
          background
          layout="prev, pager, next, total"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>

    <!-- 预览对话框 -->
    <el-dialog v-model="previewVisible" title="记录详情" width="85%" top="5vh">
      <div v-if="currentRecord" class="detail-body">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="记录码">{{ currentRecord.id }}</el-descriptions-item>
          <el-descriptions-item label="上传时间">{{ formatTime(currentRecord.reportTime) }}</el-descriptions-item>
          <el-descriptions-item label="上传人">{{ currentRecord.reporter || '（未填）' }}</el-descriptions-item>
          <el-descriptions-item label="状态">{{ currentRecord.status }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">{{ currentRecord.description || '（无）' }}</el-descriptions-item>
        </el-descriptions>

        <div class="media-section">
          <h4>照片 ({{ (currentRecord.images || []).length }}) / 视频 ({{ (currentRecord.videos || []).length }})</h4>
          <div class="media-thumbs">
            <div v-for="(m, i) in allMedia" :key="i" class="thumb" :class="{ active: previewIndex === i }" @click="previewIndex = i">
              <img v-if="m.type === 'image'" :src="m.url" />
              <div v-else class="thumb-video"><el-icon :size="24"><VideoPlay /></el-icon></div>
              <span class="thumb-label">{{ i + 1 }}</span>
            </div>
          </div>
          <div v-if="allMedia.length" class="media-stage">
            <img v-if="currentMedia.type === 'image'" :src="currentMedia.url" class="preview-img" />
            <video v-else :src="currentMedia.url" controls class="preview-video"></video>
            <div class="media-nav">
              <el-button size="small" :disabled="previewIndex === 0" @click="previewIndex--">上一张</el-button>
              <span>{{ previewIndex + 1 }} / {{ allMedia.length }}</span>
              <el-button size="small" :disabled="previewIndex >= allMedia.length - 1" @click="previewIndex++">下一张</el-button>
              <el-button size="small" type="primary" v-if="currentMedia.type === 'image'" @click="downloadCurrent">下载</el-button>
            </div>
          </div>
          <p v-else class="no-media">该记录没有照片或视频</p>
        </div>
      </div>
    </el-dialog>

    <!-- 移动端安全巡检平台 · 二维码 -->
    <el-dialog v-model="qrVisible" title="移动端安全巡检平台（手机扫码访问）" width="320px" align-center>
      <div class="qr-body">
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="二维码" class="qr-img" />
        <p class="qr-url">{{ MOBILE_URL }}</p>
      </div>
      <template #footer>
        <el-button @click="qrVisible = false">关闭</el-button>
        <el-button type="primary" @click="copyMobileUrl">复制网址</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import QRCode from 'qrcode'
import {
  loginAdmin, fetchHazards, updateHazard, deleteHazards,
  exportHazards, getImageUrl, getVideoUrl, getDownloadUrl, markSynced
} from '../api'
import auth from '../api/authState'
import dayjs from 'dayjs'

// 移动端安全巡检平台（可在构建时用 VITE_MOBILE_URL 覆盖）
const MOBILE_URL = (import.meta.env.VITE_MOBILE_URL || 'https://mob-sec-patrol.tuzi-dx.online').replace(/\/+$/, '')
const qrVisible = ref(false)
const qrDataUrl = ref('')

function openMobile() {
  window.open(MOBILE_URL, '_blank', 'noopener')
}

async function showQr() {
  if (!qrDataUrl.value) {
    try {
      qrDataUrl.value = await QRCode.toDataURL(MOBILE_URL, {
        width: 240,
        margin: 1,
        color: { dark: '#1a73e8', light: '#ffffff' }
      })
    } catch (e) {
      ElMessage.error('二维码生成失败：' + (e.message || ''))
      return
    }
  }
  qrVisible.value = true
}

function copyMobileUrl() {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(MOBILE_URL).then(() => ElMessage.success('网址已复制')).catch(() => ElMessage.warning('复制失败，请手动复制'))
  } else {
    ElMessage.warning('当前浏览器不支持自动复制，请手动复制网址')
  }
}

// 登录
// 登录态由 api/authState 统一维护：token 失效（401）时这里会自动变为 false，登录卡片立即显示
const isLoggedIn = computed(() => !!auth.token.value)
const loginLoading = ref(false)
const loginError = ref('')
const loginFormRef = ref(null)
const loginForm = reactive({ username: '', password: '' })
const loginRules = {
  username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

async function handleLogin() {
  if (!loginFormRef.value) return
  const valid = await loginFormRef.value.validate().catch(() => false)
  if (!valid) return
  loginLoading.value = true
  loginError.value = ''
  try {
    const result = await loginAdmin(loginForm)
    auth.login(result.token, result.username || loginForm.username)
    await loadRecords()
  } catch (e) {
    loginError.value = e.message || '登录失败'
  } finally {
    loginLoading.value = false
  }
}

function logout() {
  auth.logout()
}

// 列表
const records = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)
const selectedIds = ref(new Set())

const filters = reactive({
  startDate: '', endDate: '', status: '', synced: ''
})

async function loadRecords() {
  try {
    const params = { page: page.value, pageSize: pageSize.value }
    if (filters.startDate) params.startDate = filters.startDate
    if (filters.endDate) params.endDate = filters.endDate
    if (filters.status) params.status = filters.status
    if (filters.synced !== '') params.synced = filters.synced
    const data = await fetchHazards(params)
    records.value = data.data || data.records || []
    total.value = data.total || 0
  } catch (e) {
    ElMessage.error('加载失败：' + e.message)
  }
}

function applyFilter() { page.value = 1; loadRecords() }
function resetFilter() {
  filters.startDate = ''; filters.endDate = ''; filters.status = ''; filters.synced = ''
  page.value = 1; loadRecords()
}
function onPageChange(p) { page.value = p; loadRecords() }

function onSelectionChange(rows) {
  selectedIds.value = new Set(rows.map(r => r.id))
}

function formatTime(t) {
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
}

function statusTag(s) {
  if (s === '已处理') return 'success'
  if (s === '处理中') return 'warning'
  return 'info'
}

// 删除
async function handleDelete() {
  if (selectedIds.value.size === 0) return
  try {
    await ElMessageBox.confirm(
      `确认删除选中的 ${selectedIds.value.size} 条记录？删除后不可恢复。`,
      '确认删除',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
    )
    const ids = Array.from(selectedIds.value)
    await deleteHazards(ids)
    ElMessage.success('删除成功')
    selectedIds.value.clear()
    loadRecords()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error('删除失败：' + e.message)
  }
}

// 导出
async function exportCSV() { doExport('csv') }
async function exportJSON() { doExport('json') }
async function doExport(format) {
  try {
    const params = { format }
    if (filters.startDate) params.startDate = filters.startDate
    if (filters.endDate) params.endDate = filters.endDate
    if (filters.status) params.status = filters.status
    const resp = await exportHazards(params)
    const blob = await resp.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `information-gathering.${format}`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('导出失败：' + e.message)
  }
}

// 预览
const previewVisible = ref(false)
const currentRecord = ref(null)
const previewIndex = ref(0)

const allMedia = computed(() => {
  if (!currentRecord.value) return []
  const list = []
  const imgs = currentRecord.value.images || (currentRecord.value.imagePath ? [currentRecord.value.imagePath] : [])
  imgs.forEach((_, i) => list.push({ type: 'image', url: getImageUrl(currentRecord.value.id, i) }))
  const vids = currentRecord.value.videos || []
  vids.forEach((_, i) => list.push({ type: 'video', url: getVideoUrl(currentRecord.value.id, i) }))
  return list
})

const currentMedia = computed(() => allMedia.value[previewIndex.value] || {})

function previewRecord(row) {
  currentRecord.value = row
  previewIndex.value = 0
  previewVisible.value = true
}

function downloadCurrent() {
  if (!currentRecord.value || currentMedia.value.type !== 'image') return
  const url = getDownloadUrl(currentRecord.value.id, previewIndex.value)
  const a = document.createElement('a')
  a.href = url
  a.download = `${currentRecord.value.id}_${previewIndex.value + 1}.jpg`
  a.click()
}

onMounted(() => {
  // token 的初始值由 api/authState 从 localStorage 读取，这里只需按登录态决定是否加载列表
  if (isLoggedIn.value) {
    loadRecords()
  }
})
</script>

<style scoped>
.admin-page { min-height: 100vh; background: #f5f7fa; }

.login-wrap {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.login-card {
  background: #fff; border-radius: 16px; padding: 32px; width: 380px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
}
.login-card h2 { text-align: center; margin-bottom: 24px; color: #303133; display: flex; align-items: center; justify-content: center; gap: 8px; }
.login-error { color: #f56c6c; text-align: center; margin-top: 12px; font-size: 13px; }

.admin-body { max-width: 1200px; margin: 0 auto; padding: 20px; }
.admin-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
.admin-header h2 { margin: 0; color: #303133; }
.header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.filter-bar { display: flex; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; align-items: center; background: #fff; padding: 12px 16px; border-radius: 8px; }
.stats { margin-bottom: 12px; color: #606266; font-size: 13px; }

.table-wrap { background: #fff; border-radius: 8px; overflow: hidden; }
.pager { margin-top: 16px; display: flex; justify-content: center; }

.detail-body { padding: 0 8px; }
.media-section { margin-top: 20px; }
.media-section h4 { margin: 0 0 12px; color: #303133; }
.media-thumbs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.thumb {
  position: relative; width: 72px; height: 72px; border-radius: 6px; overflow: hidden;
  border: 2px solid transparent; cursor: pointer; background: #f5f7fa;
}
.thumb.active { border-color: #409eff; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.thumb-video { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #303133; color: #fff; }
.thumb-label { position: absolute; bottom: 2px; right: 4px; font-size: 10px; color: #fff; text-shadow: 0 1px 2px rgba(0,0,0,0.8); }

.media-stage { text-align: center; background: #000; border-radius: 8px; padding: 12px; }
.preview-img { max-width: 100%; max-height: 50vh; object-fit: contain; border-radius: 4px; }
.preview-video { max-width: 100%; max-height: 50vh; border-radius: 4px; }
.media-nav { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; color: #fff; }
.no-media { text-align: center; color: #909399; padding: 40px 0; }

/* ===== 移动端安全巡检平台 · 功能卡片 ===== */
.login-mobile-line {
  display: flex; align-items: center; justify-content: center; gap: 4px;
  color: #667eea; font-size: 13px; cursor: pointer; margin-top: 12px;
}
.login-mobile-line:hover { color: #409eff; text-decoration: underline; }

.mobile-card {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  background: linear-gradient(120deg, #667eea 0%, #764ba2 60%, #a855f7 100%);
  color: #fff; border-radius: 12px; padding: 14px 16px; margin-bottom: 14px;
  box-shadow: 0 6px 18px rgba(102, 126, 234, 0.25);
}
.mc-left { flex: 1; min-width: 220px; }
.mc-title { font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 6px; }
.mc-desc { font-size: 12px; opacity: 0.92; margin-top: 5px; line-height: 1.6; }
.mc-actions { display: flex; gap: 8px; flex-shrink: 0; }
.mc-actions .el-button { border-radius: 8px; }

.qr-body { text-align: center; padding: 4px 0; }
.qr-img { width: 200px; height: 200px; border: 1px solid #e4e7ed; border-radius: 8px; }
.qr-url {
  margin-top: 12px; font-size: 13px; color: #1a73e8; word-break: break-all;
  font-family: 'Courier New', monospace; user-select: all;
}
</style>
